/**
 * SeniorCalendarVoiceChat - A voice chat with all available tools
 * 
 * This implementation provides:
 * - Calendar tools (time, events)
 * - Knowledge graph tools (entities, relations)
 * - Chat control tools (stop chat)
 * - Any future tools added to the system
 */
import { VoiceChatBase, type VoiceChatOptions } from '../core/VoiceChatBase';
import { registerCalendarTools, getUpcomingCalendarEvents } from '../tools/calendarTools';
import { registerKnowledgeGraphTools } from '../tools/knowledgeGraphTools';
import { registerChatControlTools } from '../tools/chatControlTools';
import { toolRegistry, type ToolOverridesMap, ToolName } from '../tools/ToolRegistry';
import { useCalendarStore } from '~/stores/useCalendarStore';
import { useKnowledgeGraphStore } from '~/stores/knowledgeGraphStore';
import { useLogger } from '~/composables/useLogger';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '~/stores/authStore';
import { usePreferencesStore, PREFERENCE_KEYS } from '~/stores/preferencesStore';

export class SeniorCalendarVoiceChat extends VoiceChatBase {
  private i18n = useI18n();
  private authStore = useAuthStore();
  private preferencesStore = usePreferencesStore();
  private isFirstTime = false;
  private logger = useLogger();
  
  /**
   * Create a new full-featured voice chat instance with optional tool overrides
   * 
   * @param options Configuration options for the voice chat
   */
  constructor(options: VoiceChatOptions = {}) {
    // Set default options specific to senior users
    const seniorOptions: VoiceChatOptions = {
      ...options,
      inactivityTimeoutMs: 30000, // 30 seconds for senior users
    };
    
    // Call super first with enhanced options
    super(seniorOptions);
    
    // Initialize logging after super
    this.logger.info('SeniorCalendarVoiceChat: Initializing with options', { 
      hasToolOverrides: !!options.toolOverrides,
      inactivityTimeoutMs: seniorOptions.inactivityTimeoutMs
    });
    
    // Initialize tool overrides after super
    const defaultToolOverrides: ToolOverridesMap = {
      // Make calendar tools more contextual for this implementation
      [ToolName.GET_CALENDAR_EVENTS]: {
        description: this.i18n.t('pages.senior_calendar_voice_chat.tools.get_calendar_events.description'),
        continuationInstructions: this.i18n.t('pages.senior_calendar_voice_chat.tools.get_calendar_events.continuation')
      },
      // Make stop chat more appropriate for seniors
      [ToolName.STOP_CHAT]: {
        description: this.i18n.t('pages.senior_calendar_voice_chat.tools.stop_chat.description'),
        continuationInstructions: this.i18n.t('pages.senior_calendar_voice_chat.tools.stop_chat.continuation')
      }
    };
    
    // Merge default overrides with any provided in options
    const mergedOverrides = {
      ...defaultToolOverrides,
      ...(options.toolOverrides || {})
    };
    
    // Update tool overrides
    this.setToolOverrides(mergedOverrides);
    
    this.logger.info('SeniorCalendarVoiceChat: Tool overrides set, starting first-time check');
    
    // Initialize the first-time check - actual check happens asynchronously
    this.logger.info('SeniorCalendarVoiceChat: Will initialize first-time check');
    this.initializeFirstTimeCheck();
  }
  
  /**
   * Initialize the first-time check asynchronously
   */
  private async initializeFirstTimeCheck(): Promise<void> {
    this.logger.info('SeniorCalendarVoiceChat: Starting first-time check initialization');
    
    try {
      await this.checkIfFirstTime();
      this.logger.info('SeniorCalendarVoiceChat: First-time check completed successfully', {
        isFirstTime: this.isFirstTime
      });
    } catch (error) {
      this.logger.error('SeniorCalendarVoiceChat: Error checking if first time using senior voice chat', error);
      // Default to false if there's an error
      this.isFirstTime = false;
    }
  }
  
  /**
   * Check if this is the first time the user is using senior voice chat
   * Only applicable if not locked to senior view
   */
  private async checkIfFirstTime(): Promise<void> {
    const isLocked = this.authStore.isLockedToUserDevice;
    this.logger.info('SeniorCalendarVoiceChat: Checking if first time', {
      isLockedToUserDevice: isLocked,
      userId: this.authStore.userId
    });
    
    // If locked to senior view, don't use first-time experience
    if (isLocked) {
      this.logger.info('SeniorCalendarVoiceChat: Device is locked to senior view, skipping first-time experience');
      this.isFirstTime = false;
      return;
    }
    
    const hasUsedSeniorChat = await this.preferencesStore.hasValue(
      PREFERENCE_KEYS.SENIOR_VOICE_CHAT_FIRST_TIME
    );
    
    this.logger.info('SeniorCalendarVoiceChat: Checked preference store', {
      hasUsedSeniorChat,
      preferenceKey: PREFERENCE_KEYS.SENIOR_VOICE_CHAT_FIRST_TIME
    });
    
    // If the user hasn't used senior chat before, mark as first time
    this.isFirstTime = !hasUsedSeniorChat;

    // If this is the first time, mark it as used for next time
    if (this.isFirstTime) {
      this.logger.info('SeniorCalendarVoiceChat: First time detected, marking preference');
      await this.preferencesStore.setValue(
        PREFERENCE_KEYS.SENIOR_VOICE_CHAT_FIRST_TIME,
        'true'
      );
      this.logger.info('SeniorCalendarVoiceChat: Preference has been set for future sessions');
    } else {
      this.logger.info('SeniorCalendarVoiceChat: Not first time, using standard experience');
    }
  }
  
  /**
   * Get initial prompt for the full-featured assistant
   */
  public getInitialPrompt(): string {
    const seniorName = this.authStore.userPerson?.firstname || '';
    
    // Get current date and time for the prompt
    const now = new Date();
    const formattedDateTime = now.toLocaleString(); // Format according to user's locale
    // TODO: understand timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateTimeInfo = `${formattedDateTime} (${timezone})`;

    const calendarEventsString = getUpcomingCalendarEvents(timezone);
    
    this.logger.info('SeniorCalendarVoiceChat: Getting initial prompt', {
      isFirstTime: this.isFirstTime,
      seniorName,
      dateTime: dateTimeInfo,
      calendarEventsString
    });
    
    // Return different prompts based on whether it's the first time
    if (this.isFirstTime) {
      this.logger.info('SeniorCalendarVoiceChat: Using first-time prompt');
      return this.i18n.t('pages.senior_calendar_voice_chat.first_time_prompt', { 
        name: seniorName,
        dateTime: dateTimeInfo
      });
    } else {
      this.logger.info('SeniorCalendarVoiceChat: Using standard prompt', {
        seniorName: seniorName,
        dateTime: dateTimeInfo,
        calendarEventsString: calendarEventsString,
        timezone: timezone
      });
      return this.i18n.t('pages.senior_calendar_voice_chat.initial_prompt', { 
        name: seniorName,
        dateTime: dateTimeInfo,
        events: calendarEventsString, 
        timezone: timezone
      });
    }
  }

  /**
   * Register all available tools
   */
  protected async registerTools(): Promise<void> {
    this.logger.info('SeniorCalendarVoiceChat: Registering tools');
    
    // Get stores
    const calendarStore = useCalendarStore();
    
    // Register calendar tools without the time tool (passing false to exclude edit tools)
    this.logger.info('SeniorCalendarVoiceChat: Registering calendar tools');
    registerCalendarTools(calendarStore, false, false);
    
    // Register chat control tools with callback to this instance's initiateShutdown method
    this.logger.info('SeniorCalendarVoiceChat: Registering chat control tools');
    registerChatControlTools(this.initiateShutdown.bind(this));
    
    // Mark registry as initialized
    toolRegistry.setInitialized();
    
    // Log registered tools
    const registeredTools = toolRegistry.getRegisteredToolNames();
    this.logger.info(`SeniorCalendarVoiceChat: Registered ${registeredTools.length} tools: ${registeredTools.join(', ')}`);
  }  
} 